/**
 * 🔊 OVOZLI SINOV pleyeri (backend: services/narrate.js, /internal/narrate; docs/knowledge/191).
 * Agent sinovi `narrate` qilganda (faqat operator ovozli sinov so'ragan bo'lsa) — tanlangan loyiha uchun ekran pastida
 * subtitr va ovoz. Navbat bilan o'qiladi (bir gap tugamay ikkinchisi boshlanmaydi). 🔇 tanlovi eslab qolinadi.
 *
 * 🪟 Ikki oyna (2026-10-29): asosiy oyna (`where='main'`) va /emu oynasi (`where='emu'`) ikkalasida ham subtitr chiqadi,
 * OVOZ esa faqat bittasida — aks holda bir gap ikki marta eshitiladi. Qaysi oyna gapirishi `kh_narr_audio_owner`
 * (default 'main'); /emu dagi 🔊 tugmasi ovozni shu oynaga oladi, 🔇 — asosiy oynaga qaytaradi.
 */
import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const audioOwner = () => { try { return localStorage.getItem('kh_narr_audio_owner') === 'emu' ? 'emu' : 'main'; } catch (e) { return 'main'; } };

export default function NarrationPlayer({ apiUrl, token, projectId, where = 'main' }) {
  const [cur, setCur] = useState(null);           // { text, url, seconds }
  const [muted, setMutedRaw] = useState(() => { try { return localStorage.getItem('kh_narr_mute') === '1'; } catch (e) { return false; } });
  const [blocked, setBlocked] = useState(false);  // brauzer avtomatik ovozni bloklagan — bir bosish kerak
  const queueRef = useRef([]);
  const playingRef = useRef(false);
  const audioRef = useRef(null);
  const [owner, setOwnerRaw] = useState(audioOwner);
  const mutedRef = useRef(muted); mutedRef.current = muted;
  const pidRef = useRef(projectId); pidRef.current = projectId;
  const hideRef = useRef(null);
  const setMuted = (v) => { setMutedRaw(v); try { localStorage.setItem('kh_narr_mute', v ? '1' : '0'); } catch (e) { } if (v && audioRef.current) { try { audioRef.current.pause(); } catch (e) { } } };

  const next = () => {
    const item = queueRef.current.shift();
    if (!item) { playingRef.current = false; clearTimeout(hideRef.current); hideRef.current = setTimeout(() => setCur(null), 4000); return; }
    playingRef.current = true; clearTimeout(hideRef.current);
    setCur(item);
    const fallbackMs = Math.max(1500, (Number(item.seconds) || item.text.split(/\s+/).length * 0.45) * 1000) + 300;
    let done = false;
    const finish = () => { if (done) return; done = true; next(); };
    const timer = setTimeout(finish, fallbackMs + 1500);
    if (!mutedRef.current && item.url && audioOwner() === where) {
      const a = new Audio(`${apiUrl || ''}${item.url}`);
      audioRef.current = a;
      a.onended = () => { clearTimeout(timer); finish(); };
      a.onerror = () => { };
      a.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
    }
  };

  useEffect(() => {
    if (!token) return undefined;
    const s = io(apiUrl || '', { auth: { token } });
    s.on('qa:narration', (d) => {
      if (!d || !d.text || Number(d.projectId) !== Number(pidRef.current)) return;
      queueRef.current.push({ text: d.text, url: d.url, seconds: d.seconds });
      if (queueRef.current.length > 6) queueRef.current.splice(0, queueRef.current.length - 6);   // kechikib qolmasin
      if (!playingRef.current) next();
    });
    return () => { s.disconnect(); clearTimeout(hideRef.current); if (audioRef.current) { try { audioRef.current.pause(); } catch (e) { } } };
  }, [apiUrl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ikkinchi oyna ovoz egasini almashtirsa — bu oynadagi belgi ham yangilansin (`storage` faqat BOSHQA oynadan keladi).
  useEffect(() => {
    const on = (e) => { if (e.key === 'kh_narr_audio_owner') setOwnerRaw(audioOwner()); };
    window.addEventListener('storage', on);
    return () => window.removeEventListener('storage', on);
  }, []);

  // /emu: 🔊 = ovoz shu oynada, 🔇 = ovoz asosiy oynada (bu yerda faqat subtitr).
  const emuSound = where === 'emu' && owner === 'emu' && !muted;
  const toggleEmu = () => {
    const v = owner === 'emu' ? 'main' : 'emu';
    try { localStorage.setItem('kh_narr_audio_owner', v); } catch (e) { }
    setOwnerRaw(v);
    if (v === 'emu' && muted) setMuted(false);
    if (v !== 'emu' && audioRef.current) { try { audioRef.current.pause(); } catch (e) { } }
  };
  if (!cur) return null;
  if (where === 'emu') {
    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] max-w-[min(42rem,80vw)] flex items-start gap-2 rounded-xl bg-gray-900/90 text-white px-4 py-2.5 shadow-lg backdrop-blur pointer-events-auto">
        <button type="button" onClick={toggleEmu} title={emuSound ? "Ovoz shu oynada — bossangiz ovoz asosiy oynaga o'tadi (bu yerda faqat subtitr)" : "Bu yerda faqat subtitr (ovoz asosiy oynada) — bossangiz ovoz shu oynada chiqadi"}
          className="shrink-0 text-lg leading-none mt-0.5">{emuSound ? '🔊' : '🔇'}</button>
        <div className="text-sm leading-snug">{cur.text}</div>
        {blocked && emuSound && (
          <button type="button" onClick={() => { setBlocked(false); if (audioRef.current) audioRef.current.play().catch(() => setBlocked(true)); }}
            className="shrink-0 text-xs px-2 py-0.5 rounded bg-white/20 hover:bg-white/30">▶ ovoz</button>
        )}
      </div>
    );
  }
  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[70] max-w-[min(42rem,92vw)] flex items-start gap-2 rounded-xl bg-gray-900/90 text-white px-4 py-2.5 shadow-lg backdrop-blur">
      <button type="button" onClick={() => {
          if (owner === 'emu') { try { localStorage.setItem('kh_narr_audio_owner', 'main'); } catch (e) { } setOwnerRaw('main'); if (muted) setMuted(false); return; }
          setMuted(!muted);
        }} title={owner === 'emu' ? "Ovoz /emu oynasida — bossangiz shu oynaga qaytadi" : muted ? "Ovozni yoqish" : "Ovozni o'chirish (subtitr qoladi)"}
        className="shrink-0 text-lg leading-none mt-0.5">{muted || owner === 'emu' ? '🔇' : '🔊'}</button>
      <div className="text-sm leading-snug">{cur.text}</div>
      {blocked && !muted && (
        <button type="button" onClick={() => { setBlocked(false); if (audioRef.current) audioRef.current.play().catch(() => setBlocked(true)); }}
          className="shrink-0 text-xs px-2 py-0.5 rounded bg-white/20 hover:bg-white/30">▶ ovoz</button>
      )}
    </div>
  );
}
